import {
  AutocompleteArrayInput,
  AutocompleteInput,
  ReferenceArrayInput,
  ReferenceInput,
  TextInput,
  required,
} from 'react-admin';

import { NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { genericfilterToQuery } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { getTaxonomyFullName } from '@app/utils/taxonomyUtils';
import { Grid } from '@mui/material';

import { CustomRichText } from '@app/components/CustomRichText';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { TaxonomyPartnerHeroImage } from '../TaxonomyPartnerHeroImage';

interface TaxonomyFormProps {
  creating?: boolean;
  selectedTaxonomy?: GenericObject | null;
}

export const TaxonomyForm = ({ selectedTaxonomy, creating = false }: TaxonomyFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();

  const taxonomyId = selectedTaxonomy?.id as number;
  const showImageUpload = !creating && (taxonomyId === 252 || taxonomyId === 258);

  return (
    <div style={{ width: '100%' }}>
      <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />
      <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
        <Grid item xs={12}>
          <ReferenceInput
            source="parent_category_id"
            reference="taxonomies"
            filter={{ level: selectedTaxonomy?.level }}>
            <AutocompleteInput
              label="Parent Topic"
              variant="outlined"
              disabled={creating}
              helperText={false}
              fullWidth
              optionText={(record) => {
                return getTaxonomyFullName(record);
              }}
              filterToQuery={genericfilterToQuery}
            />
          </ReferenceInput>
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            source="category_name"
            label="Topic Name"
            variant="outlined"
            fullWidth
            validate={[required()]}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            source="nav_label"
            label="Nav Label"
            variant="outlined"
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            variant="outlined"
            helperText={false}
            Component={CustomRichText}
            language={language}
            label="Description"
            source="description"
          />
        </Grid>
        <Grid item xs={12}>
          <ReferenceArrayInput
            source="related_topic_ids"
            filter={{ excludedId: selectedTaxonomy?.id }}
            reference="taxonomies">
            <AutocompleteArrayInput
              variant="outlined"
              helperText={false}
              label="Related Topics"
              fullWidth
              filterToQuery={genericfilterToQuery}
              optionText={(record) => (record?.category_name ? record?.category_name[DEFAULT_LANGUAGE] : '')}
            />
          </ReferenceArrayInput>
        </Grid>

        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            source="seo_title"
            label="SEO Title"
            variant="outlined"
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            variant="outlined"
            label="Meta Description"
            multiline
            helperText={false}
            rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
            source="meta_description"
            fullWidth
          />
        </Grid>

        {showImageUpload && (
          <Grid item xs={12}>
            <TaxonomyPartnerHeroImage taxonomyId={taxonomyId} />
          </Grid>
        )}
      </Grid>
    </div>
  );
};
